import React, { useState, useEffect, useRef } from 'react';
import { startOfWeek, endOfWeek } from 'date-fns';
import { Box, Card, CardContent, Grid, Typography, Button, IconButton, Alert, CircularProgress } from '@mui/material';
import { CalendarToday, BarChart, Download as DownloadIcon, HelpOutline } from '@mui/icons-material';
import { useTour } from '../../../UserGuide/TourProvider';
import SurveyProgress from '../SurveyProgress/SurveyProgress';
import ToDoList from '../ToDoList/ToDoList';
import DailySurveyChart from '../DailySurveyChart/DailySurveyChart';
import PerformanceChart from '../PerformanceChart';
import PerformanceChecklist from '../ObservationPeriod/PerformanceCheckList';
import ObservationPeriods from '../ObservationPeriod/ObservationPeriod';
import SurveyCalendar from '../SurveyProgress/SurveyCalendar';
import SurveyProgressBar from '../SurveyProgressBar/SurveyProgressBar';
import RestartObservationPeriod from '../RestartObservationPeriod/RestartObservationPeriod';
import './Overview.css';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { getScheduledNotificationForObservationPeriod } from '../../../../services/notificationsService';
import * as XLSX from 'xlsx';
import PreviewAndDownload from '../PreviewAndDownload';

const Overview = ({
    userId,
    setContent,
    dailySurveyResponses = [],
    performanceResponses = [],
    noData,
    isAdmin,
    refreshData,
    syncStatus,
}) => {
    const [currentWeek, setCurrentWeek] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const [showCalendar, setShowCalendar] = useState(false);
    const [showProgressBar, setShowProgressBar] = useState(false);
    const [observationPeriods, setObservationPeriods] = useState([]);
    const [selectedObservationPeriod, setSelectedObservationPeriod] = useState(null);
    const [isInObservationPeriod, setIsInObservationPeriod] = useState(false);
    const [syncAlertOpen, setSyncAlertOpen] = useState(true);
    const surveyChartRefs = useRef([]);
    const { t } = useTranslation();
    const { startPageTour } = useTour();
    
    const handleStartTour = () => {
        if (startPageTour) {
            startPageTour('overview');
        }
    };


    useEffect(() => {
        const fetchObservationPeriods = async () => {
            // Don't make API calls if userId is not provided or invalid
            if (!userId || userId === 'undefined') {
                console.warn('Overview: userId is not defined, skipping data fetch');
                setObservationPeriods([]);
                setIsInObservationPeriod(false);
                return;
            }

            try {
                const response = await getScheduledNotificationForObservationPeriod(userId);
                const periods = response.dateRanges || [];
                setObservationPeriods(periods);

                // Check if user is currently in an observation period
                // Filter out archived periods
                if (periods.length > 0) {
                    const currentDate = new Date();
                    const activePeriod = periods.find(period => {
                        // Skip archived periods
                        if (period.isArchived) return false;

                        const startDate = new Date(period.startDate);
                        const endDate = new Date(period.endDate);
                        return currentDate >= startDate && currentDate <= endDate;
                    });
                    setIsInObservationPeriod(!!activePeriod);
                } else {
                    setIsInObservationPeriod(false);
                }
            } catch (error) {
                console.error('Error fetching observation periods:', error);
                setIsInObservationPeriod(false);
            }
        };

        fetchObservationPeriods();
    }, [userId]);

    // Re-show the sync alert whenever a new sync result arrives.
    useEffect(() => {
        setSyncAlertOpen(true);
    }, [syncStatus]);




    const handleExport = async () => {
        if (!userId || userId === 'undefined') {
            toast.error('User ID is not available. Cannot export data.');
            return;
        }

        if (performanceResponses.length === 0) {
            toast.warn(t('overview.noPerformanceData'));
            return;
        }

        try {
            // Create workbook
            const workbook = XLSX.utils.book_new();

            // 1. Performance Data Sheet
            const sortedResponses = [...performanceResponses].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
            const performanceData = sortedResponses.map((entry) => {
                const responseDate = new Date(entry.timestamp);
                return {
                    Date: responseDate.toLocaleDateString(),
                    Time: responseDate.toLocaleTimeString(),
                    Score: entry.score,
                };
            });
            const performanceSheet = XLSX.utils.json_to_sheet(performanceData);
            XLSX.utils.book_append_sheet(workbook, performanceSheet, 'Performance');

            // Fetch health data and daily questions
            const token = localStorage.getItem('token');
            const headers = {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            };

            console.log('Fetching health data...');
            const healthDataResponse = await fetch(`${process.env.REACT_APP_API_URL}/healthdata/${userId}`, { headers });
            console.log('Health data response status:', healthDataResponse.status);
            const healthData = healthDataResponse.ok ? await healthDataResponse.json() : null;
            console.log('Health data:', healthData);

            console.log('Fetching daily questions...');
            const dailyQuestionsResponse = await fetch(`${process.env.REACT_APP_API_URL}/daily-question/${userId}`, { headers });
            console.log('Daily questions response status:', dailyQuestionsResponse.status);
            const dailyQuestionsData = dailyQuestionsResponse.ok ? await dailyQuestionsResponse.json() : null;
            console.log('Daily questions data:', dailyQuestionsData);

            // 2. Heart Rate Sheet (hourly averages)
            if (healthData?.heartRateData && healthData.heartRateData.length > 0) {
                const heartRateExportData = [];

                healthData.heartRateData.forEach(dayData => {
                    // Group by hour and calculate averages
                    const hourlyData = {};

                    dayData.values.forEach(({ timestamp, value }) => {
                        const date = new Date(timestamp);
                        const hour = date.getHours();
                        const dateStr = date.toLocaleDateString();
                        const hourKey = `${dateStr} ${hour}:00`;

                        if (!hourlyData[hourKey]) {
                            hourlyData[hourKey] = { sum: 0, count: 0, date: dateStr, hour: `${hour}:00` };
                        }
                        hourlyData[hourKey].sum += value;
                        hourlyData[hourKey].count += 1;
                    });

                    // Calculate averages
                    Object.values(hourlyData).forEach(({ date, hour, sum, count }) => {
                        heartRateExportData.push({
                            Date: date,
                            Hour: hour,
                            'Average Heart Rate (bpm)': Math.round(sum / count)
                        });
                    });
                });

                if (heartRateExportData.length > 0) {
                    const heartRateSheet = XLSX.utils.json_to_sheet(heartRateExportData);
                    XLSX.utils.book_append_sheet(workbook, heartRateSheet, 'Heart Rate');
                }
            }

            // 3. HRV Sheet
            if (healthData?.hrvData && healthData.hrvData.length > 0) {
                const hrvExportData = [];

                healthData.hrvData.forEach(dayData => {
                    dayData.values.forEach(({ timestamp, value }) => {
                        const date = new Date(timestamp);
                        hrvExportData.push({
                            Date: date.toLocaleDateString(),
                            Time: date.toLocaleTimeString(),
                            'HRV (ms)': Math.round(value)
                        });
                    });
                });

                if (hrvExportData.length > 0) {
                    const hrvSheet = XLSX.utils.json_to_sheet(hrvExportData);
                    XLSX.utils.book_append_sheet(workbook, hrvSheet, 'HRV');
                }
            }

            // 4. Sleep Data Sheet (with formatted duration)
            if (healthData?.sleepData && healthData.sleepData.length > 0) {
                const sleepExportData = healthData.sleepData.map(sleep => {
                    const sessionStart = new Date(sleep.sessionStart);
                    const sessionEnd = new Date(sleep.sessionEnd);

                    // totalDuration is stored in MINUTES (not seconds!)
                    const totalHours = Math.floor(sleep.totalDuration / 60);
                    const remainingMinutes = sleep.totalDuration % 60;
                    const durationFormatted = `${totalHours}h ${remainingMinutes}m`;

                    return {
                        Date: new Date(sleep.date).toLocaleDateString(),
                        'Session Start': sessionStart.toLocaleString(),
                        'Session End': sessionEnd.toLocaleString(),
                        'Total Duration': durationFormatted,
                        'Duration (hours)': (sleep.totalDuration / 60).toFixed(2)
                    };
                });

                const sleepSheet = XLSX.utils.json_to_sheet(sleepExportData);
                XLSX.utils.book_append_sheet(workbook, sleepSheet, 'Sleep');
            }

            // 5. Sleep Quality Sheet (from daily questions)
            if (dailyQuestionsData?.responses && dailyQuestionsData.responses.length > 0) {
                const sleepQualityData = dailyQuestionsData.responses.map(response => ({
                    Date: new Date(response.date).toLocaleDateString(),
                    'Wake Up Time': response.wakeUpTime,
                    'Bed Time': response.bedTime,
                    'Sleep Quality (1-10)': response.sleepQuality,
                    'Synced from Smartwatch': response.syncedFromSmartwatch ? 'Yes' : 'No'
                }));

                const sleepQualitySheet = XLSX.utils.json_to_sheet(sleepQualityData);
                XLSX.utils.book_append_sheet(workbook, sleepQualitySheet, 'Sleep Quality');
            }

            // Write file
            const timestamp = new Date().toISOString().split('T')[0];
            XLSX.writeFile(workbook, `health_data_export_${userId}_${timestamp}.xlsx`);

        } catch (error) {
            console.error('Error exporting data:', error);
            console.error('Error details:', error.message);
            toast.error(`Error exporting data: ${error.message}. Please try again.`);
        }
    };

    const [refresh, setRefresh] = useState(0);
    useEffect(() => {
        const now = new Date();
        const minutesUntilNextHour = 60 - now.getMinutes();
        const millisecondsUntilNextHour = minutesUntilNextHour * 60 * 1000;

        const timeoutId = setTimeout(() => {
            setRefresh((prev) => prev + 1);

            const intervalId = setInterval(() => {
                setRefresh((prev) => prev + 1);
            }, 60 * 60 * 1000);

            return () => clearInterval(intervalId);
        }, millisecondsUntilNextHour);

        return () => clearTimeout(timeoutId);
    }, []);

    const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(currentWeek, { weekStartsOn: 1 });
    const currentWeekResponses = performanceResponses.filter((response) => {
        const responseDate = new Date(response.timestamp);
        return responseDate >= weekStart && responseDate <= weekEnd;
    });

    const handlePeriodSelect = (period) => {
        setSelectedPeriod(period);
    };

    const handleReturnToObservationPeriods = () => {
        setSelectedPeriod(null);
    };

    return (
        <Box className="overview-container">
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h4" className="overview-title">
                    {t('overview.dashboardOverview')}
                </Typography>
                <IconButton 
                    onClick={handleStartTour}
                    color="primary"
                    title={t('overview.startGuidedTour')}
                    data-tour="help-icon"
                >
                    <HelpOutline />
                </IconButton>
            </Box>
            {syncStatus && syncStatus !== 'syncing' && syncAlertOpen && (
                <Alert
                    severity={syncStatus.success ? 'success' : 'error'}
                    sx={{ mb: 2, mx: 'auto', maxWidth: 600 }}
                    onClose={() => setSyncAlertOpen(false)}
                >
                    {syncStatus.message}
                </Alert>
            )}

            <Box display="flex" justifyContent="center" alignItems="center" mb={2} data-tour="action-buttons">
                <Button
                    variant="contained"
                    color="primary"
                    onClick={refreshData}
                    style={{ marginRight: '10px' }}
                    data-tour="refresh-button"
                >
                    {t('overview.refresh')}
                </Button>
                <Box sx={{ marginRight: '10px' }}>
                    <RestartObservationPeriod 
                        userId={userId}
                        isInObservationPeriod={isInObservationPeriod}
                        onSuccess={refreshData}
                    />
                </Box>
                <PreviewAndDownload
                    isAdmin={isAdmin}
                    userId={userId}
                    performanceResponses={performanceResponses}
                    dailySurveyResponses={dailySurveyResponses}
                    observationPeriods={observationPeriods}
                    selectedObservationPeriod={selectedObservationPeriod}
                    setSelectedObservationPeriod={setSelectedObservationPeriod}
                />
            </Box>

            <Grid container spacing={3} alignItems="stretch">
            {/* First Column: Survey Progress and Observation Periods */}
            <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box display="flex" flexDirection="column" gap={2} sx={{ flex: 1 }}>
                    <Card elevation={3} className="card" data-tour="survey-progress-card">
                        <CardContent>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Typography variant="h6">
                                    {t('overview.surveyProgress')}
                                </Typography>
                                <Box>
                                    <IconButton color="primary" onClick={() => setShowCalendar(!showCalendar)} data-tour="calendar-toggle">
                                        <CalendarToday />
                                    </IconButton>
                                    <IconButton color="primary" onClick={() => setShowProgressBar(!showProgressBar)} data-tour="progress-bar-toggle">
                                        <BarChart />
                                    </IconButton>
                                    <IconButton color="primary" onClick={handleExport} data-tour="export-button">
                                        <DownloadIcon />
                                    </IconButton>
                                </Box>
                            </Box>
                            {showCalendar ? (
                                <SurveyCalendar
                                    surveyPeriods={[]}
                                    onReturn={() => setShowCalendar(false)}
                                    userId={userId}
                                />
                            ) : showProgressBar ? (
                                <SurveyProgressBar userId={userId} onReturn={() => setShowProgressBar(false)} />
                            ) : (
                                <SurveyProgress userId={userId} key={refresh} observationPeriods={observationPeriods} />
                            )}
                        </CardContent>
                    </Card>

                    <Card elevation={3} className="card" data-tour="observation-periods-card" sx={{ flex: 1 }}>
                        <CardContent>
                            {!selectedPeriod ? (
                                <ObservationPeriods userId={userId} onSelectPeriod={handlePeriodSelect} />
                            ) : (
                                <PerformanceChecklist
                                    userId={userId}
                                    startDate={selectedPeriod.start}
                                    endDate={selectedPeriod.end}
                                    completedQuestions={selectedPeriod.completedQuestions}
                                    expectedQuestions={selectedPeriod.expectedQuestions}
                                    notifications={selectedPeriod.notifications}
                                    status={selectedPeriod.status}
                                    onReturn={handleReturnToObservationPeriods}
                                />
                            )}
                        </CardContent>
                    </Card>
                </Box>
            </Grid>

            {/* Second Column: Daily Survey Chart */}
            <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column' }}>
                <Card elevation={3} className="card" sx={{ flex: 1 }} data-tour="daily-survey-chart-card">
                    <CardContent>
                        <Typography variant="h6" gutterBottom style={{ textAlign: 'center' }}>
                            {t('overview.dailySurveyChart')}
                        </Typography>
                        <DailySurveyChart
                            responses={dailySurveyResponses}
                            noData={!dailySurveyResponses || dailySurveyResponses.length === 0}
                            key={refresh}
                            chartRef={surveyChartRefs}
                            userId={userId}
                            isAdmin={isAdmin}
                        />
                    </CardContent>
                </Card>
            </Grid>

            {/* Third Column: Performance Chart and To-Do List (natural height, not stretched) */}
            <Grid item xs={12} md={4}>
                <Box display="flex" flexDirection="column" gap={2}>
                    <Card elevation={3} className="card" data-tour="performance-chart-card">
                        <CardContent>
                            <PerformanceChart
                                responses={currentWeekResponses}
                                currentWeek={currentWeek}
                                setCurrentWeek={setCurrentWeek}
                                observationPeriods={observationPeriods}
                                key={refresh}
                            />
                        </CardContent>
                    </Card>

                    <Card elevation={3} className="card" data-tour="todo-list-card">
                        <CardContent>
                            <Typography variant="h6" gutterBottom style={{ textAlign: 'center' }}>
                                {t('overview.toDoList')}
                            </Typography>
                            <ToDoList
                                userId={userId}
                                setContent={setContent}
                                isAdmin={isAdmin}
                                key={refresh}
                            />
                        </CardContent>
                    </Card>
                </Box>
            </Grid>
        </Grid>
        </Box>
    );
}
export default Overview;
